<template>
    <div class="file-block" :class="canGrab && !editing ? 'can-grab' : null">
        <fullscreen-loading v-if="loading" />

        <div
            class="image-wrapper file-block-inner"
            v-if="isImage(file) || isVideo(file)"
            :style="{backgroundImage: !autoRatio ? `url('${thumbnail}')` : null }"
            @click.prevent="clickFile"
            ref="imageWrapper"
            :key="file.id"
            :class="getFileStateClass()"
            v-tooltip="getFileErrors()"
        >
            <slot />

            <div class="processing-info" v-if="file.state === 3/* processing */">
                <span>{{ rerenderingPercentage || 0 }}%</span>
                <div class="spinner-border"></div>
            </div>
            <div class="processing-info" v-else-if="isVideo(file)">
                <span v-if="(!file.errors || !file.errors.length) && file.state !== 2/* processing */"><icon name="play"></icon></span>
                <span v-else><icon name="exclamation"></icon></span>
                <div class="normal-border"></div>
            </div>
            <div class="processing-info" v-else-if="showPlay">
                <icon scale="0.8" name="play"></icon>
            </div>

            <div class="custom-control custom-checkbox image-checkbox" v-if="canSelect">
                <input type="checkbox" class="custom-control-input" :id="'select_file_' + file.id" :checked="file.checked">
                <label class="custom-control-label" :for="'select_file_' + file.id"></label>
            </div>

            <a href="#" class="trash-file" @click="unlinkFile" v-if="showActions && actions.indexOf('unlink') !== -1">
                <icon name="times" scale="0.8"></icon>
            </a>

            <a href="#" class="trash-file" @click="deleteFile" v-if="showActions && actions.indexOf('delete') !== -1">
                <icon name="times" scale="0.8"></icon>
            </a>

            <a href="#" class="btn btn-secondary btn-sm btn-edit" @click="editFile" role="button" v-if="showActions && actions.indexOf('edit') !== -1" v-tooltip="$t('files.edit_file_tooltip')">
                <icon name="pencil-alt"></icon>
            </a>
        </div>

        <template v-if="isAudio(file)">

            <!-- File -->
            <div class="row align-items-center mb-3 shadow-none file-block-inner">
                <div class="col-auto song-block-inner">
                    <div
                        class="icon icon-shape bg-soft-warning text-warning mr-2"
                        style="background-size: cover"
                        :style="{ backgroundImage: file.song.cover ? 'url(\'' + `/storage/song-images/${file.song.cover}` + '\')' : null}">
                        <icon v-if="!file.song.cover" name="music" style="fill: #FFBE3D;"></icon>
                    </div>
                </div>
                <div class="col pl-0">
                    <div style="line-height: 14px;">
                        <span class="h6 text-sm mb-0 song-block-inner">{{ file.song.display_name || file.song.name }}</span>
                        <br />
                        <p class="d-inline-block mb-0 text-xs song-block-inner">{{ file.song.author }}</p>
                    </div>
                </div>
                <div class="col-auto actions">
                    <div class="dropdown" data-toggle="dropdown">
                        <a href="#" class="action-item" role="button" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                            <icon name="times" @click="unlinkFile"></icon>
                        </a>
                    </div>
                </div>
            </div>

            <song-player :preload="'none'" :src="`/storage/songs/${file.song.name}`" :key="file.id" />
        </template>

        <div class="file-info" v-if="!isAudio(file)">
            <div class="file-name" :title="file.name">
                {{ file.name }}
            </div>
            <div v-if="showCount">{{ file.count }} {{ $t('files.used_count') }}</div>
        </div>

        <file-edit :ceremony-code="ceremonyCode"
                   :file="file"
                   v-if="editing && block && !file.crop_data"
                   @save="save"
                   @file-updated="updatedFile"
                   @close="stopEditing"
                   :block="block"
        />
        <file-edit-old :ceremony-code="ceremonyCode"
                   :file="file"
                   v-if="editing && block && file.crop_data"
                   @save="updatedFile($event); save($event);"
                   @close="stopEditing"
                   :block-id="block.id"
        />
    </div>
</template>

<script>

    import FileMixin from '../mixins/FileMixin';
    import SongPlayer from './SongPlayer';
    import { mapState } from 'vuex';
    import FullscreenLoading from './FullscreenLoading';
    import FileEdit from './FileEdit';
    import FileEditOld from './FileEditOld';

    export default {

        components: {
            FullscreenLoading,
            SongPlayer,
            FileEdit,
            FileEditOld
        },

        mixins: [
            FileMixin,
        ],

        props: {
            ceremonyCode: {
                type: String,
                required: true,
            },
            block: {
                type: Object,
                default: null,
            },
            file: {
                type: Object,
                required: true,
            },
            canSelect: {
                type: Boolean,
                default: false,
            },
            canGrab: {
                type: Boolean,
                default: false,
            },
            canEdit: {
                type: Boolean,
                default: true,
            },
            showCount: {
                type: Boolean,
                default: false,
            },
            showActions: {
                type: Boolean,
                default: true,
            },
            showWarnings: {
                type: Boolean,
                default: true,
            },
            showPlay: {
                type: Boolean,
                default: false,
            },
            active: {
                type: Boolean,
                default: false,
            },
            autoRatio: {
                type: Boolean,
                default: false,
            },
            collapse: {
                type: Boolean,
                default: false,
            },
            backgroundPhoto: {
                type: Boolean,
                default: false,
            },
        },


        data() {
            return {
                loading: false,
                rerenderingPercentage: this.file.rerendering_percentage,
                editing: false,
            };
        },

        computed: {
            ...mapState({
                filesToStateCheck: state => state.filesToStateCheck,
            }),

            thumbnail() {
                return this.file.thumbnail_path.replace(/\/:code\//g, '/' + this.ceremonyCode + '/');
            },

            actions() {
                const actions = [];

                if (this.canEdit) {
                    if (this.file.extension === 'jpg' || this.file.extension === 'png' || this.file.extension === 'jpeg') {
                        actions.push('edit');
                    }

                    if (this.canSelect) {
                        actions.push('delete');
                    } else {
                        actions.push('unlink');
                    }
                }

                return actions;
            },
        },

        watch: {
            filesToStateCheck() {
                this.updateReRenderingStatus();
            },

            file() {
                this.updateReRenderingStatus();
            },
        },

        mounted() {
            if (this.file.state === 3/*Processing*/) {
                this.$store.dispatch('addFileForStateCheck', this.file);
            }
        },

        methods: {
            save(file) {
                this.$emit('save', file);
                this.stopEditing();
            },

            updatedFile(file) {
                this.$emit('files-updated', [file]);
            },

            updateReRenderingStatus() {
                const fileState = this.filesToStateCheck.find(file => file.id === this.file.id);

                if (!fileState) {
                    return;
                }

                this.rerenderingPercentage = fileState.rerendering_percentage;
                this.file.state = fileState.state;
                this.file.errors = fileState.errors;

                this.$emit('fileStateChanged', this.file);
            },

            getFileStateClass() {
                let className = this.autoRatio ? ' auto-ratio' : '';

                if (this.collapse) {
                    className += ' file-collapsed';
                }

                if (this.active) {
                    return 'file-active' + className;
                }

                if (this.file.state === 2/*Error*/) {
                    return 'file-failed' + className;
                }

                if (this.file.checked) {
                    return 'file-selected' + className;
                }

                if (this.file.count && this.showCount) {
                    return 'file-added' + className;
                }

                if (this.file.warnings.length && this.showWarnings) {
                    return 'file-warning' + className;
                }

                return className;
            },

            getFileErrors() {
                if (!this.showWarnings) {
                    return null;
                }

                const messages = [
                    ...(this.file.errors && this.file.errors.length ? [this.$t('general.error_contact_funeral_director')] : []),
                    ...(this.file.warnings ? this.file.warnings : []),
                ];

                if (!messages.length) {
                    return null;
                }

                return messages.join(', ');
            },

            async editFile(event) {
                event.preventDefault();
                event.stopPropagation();

                this.editing = true;
                this.$emit('is-editing', true);
            },

            async stopEditing() {
                this.editing = false;
                this.$emit('is-editing', false);
            },

            async unlinkFile(event) {
                event.preventDefault();
                event.stopPropagation();

                if (this.backgroundPhoto) {
                    // background photo doenst need an unlink
                    this.$emit('unlink', this.file);
                    return;
                }

                this.loading = true;
                try {
                    await this.$http.post(`/api/ceremonies/code/${this.ceremonyCode}/files/${this.file.id}/unlink/${this.block.id}/${this.file.pivot.id}`);

                    this.$emit('unlink', this.file);

                } catch (exception) {
                    console.error(exception);
                    console.error('Cannot unlink file');
                }

                this.loading = false;
            },

            async deleteFile(event) {
                event.preventDefault();
                event.stopPropagation();

                this.loading = true;

                try {
                    await this.$http.delete(`/api/ceremonies/code/${this.ceremonyCode}/files/${this.file.id}`);

                    this.$store.dispatch('updateCeremony', this.$route.params.code);
                    this.$emit('delete', this.file);
                } catch (exception) {
                    console.error(exception);
                    console.error('Cannot delete file');
                }

                this.loading = false;
            },

            clickFile(event) {
                if (this.canSelect && !event.ctrlKey) {
                    this.$emit('select-file', this.file.id);
                } else {
                    // failed or processing
                    if (this.file.state === 3 || this.file.state === 2) {
                        return;
                    }

                    this.$emit('click', this.file.id);
                }
            },
        },
    };

</script>

<style lang="scss">

    .file-name {
        font-size: 11px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        text-align: left;
    }

    .file-info {
        font-size: 11px;
        padding-top: 2px;
    }

    .file-actions {
        a:after {
            content: "|";
            display: inline-block;
            margin-left: 4px;
            pointer-events: none;
            color: #718096;
        }

        a:last-child:after {
            content: none;
        }
    }

    .image-wrapper {
        position: relative;
        width: 100%;
        padding-top: 100%; /* 1:1 Aspect Ratio */
        background-size: cover;
        background-position: center center;
        border-radius: 0.375rem;

        &.file-selected {
            box-shadow: 0 0 0 2px #007ce6;
        }

        &.file-active,
        &.file-added {
            box-shadow: 0 0 0 2px #5cc9a7;
        }

        &.file-failed {
            box-shadow: 0 0 0 2px #E3093B;
        }

        &.file-warning {
            box-shadow: 0 0 0 2px #ffad0a;
        }

        &.auto-ratio {
            padding-top: 0;
        }

        &.file-collapsed {
            height: 80px;
            overflow: hidden;
        }
    }

    .trash-file {
        position: absolute;
        top: -11px;
        right: -11px;
        width: 20px;
        height: 20px;
        background: #fff;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 10px;
        color: #718096;
        fill: #718096;
        box-shadow: 2px 3px 5px rgba(31, 45, 61, 0.2);
        z-index: 1;
    }

    .processing-info {
        position: absolute;
        top: 0;
        left: 0;
        background: rgba(0, 0, 0, 0.4);
        width: 100%;
        height: 100%;
        border-radius: 0.375rem;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 20px;
        color: #fff;
        font-weight: bold;

        .spinner-border {
            position: absolute;
            width: 80px;
            height: 80px;
            border: 2px solid currentColor;
            border-right-color: transparent;
        }

        .normal-border {
            position: absolute;
            width: 60px;
            height: 60px;
            border: 2px solid currentColor;
            border-radius: 30px;
            cursor: pointer;
        }

        svg {
            fill: #fff;
        }
    }

    .image-checkbox {
        position: absolute;
        top: -15px;
        left: -15px;

        .custom-control-input {
            height: 1.5rem;
            width: 1.5rem;
        }

        .custom-control-label {
            &:before {
                border: 1px solid #cdd6e6;
            }

            &:before,
            &:after {
                height: 1.5rem;
                width: 1.5rem;
                cursor: pointer;
            }
        }
    }

    .can-grab {
        cursor: grabbing;
    }

    .sortable-drag {
        .btn-edit,
        .file-info,
        .trash-file {
            display: none;
        }
    }

    .file-warnings {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(214, 160, 7, 0.6);
        text-shadow: 1px 1px 4px black;
        color: #fff;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 0.375rem;
    }

    .btn-edit {
        position: absolute;
        bottom: 5px;
        left: 5px;
        width: 20px;
        height: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0;

        svg {
            width: 12px;
        }
    }
</style>
